/**
 * BodhaPage — real React replacement for the /bodha iframe mockup.
 *
 * Staged at /bodha-v2. /bodha continues to serve the iframe mockup until
 * the founder authorises the flip.
 *
 * Consumes System B (POST /api/mycraftz-install/ideas) — the same engine
 * powering the /v1 6-step overlay. Renders exactly 10 AI-generated
 * "Wisdom Artefacts" per book. Sanskrit per idea intentionally excluded
 * (P2 backlog). Card design tightened so no visual hole remains.
 *
 * URL contract:
 *   /bodha-v2                       → empty state (designed, no redirect)
 *   /bodha-v2?book=<numeric-id>     → primary path
 *   /bodha-v2?book=<slug>           → legacy path (BookDetailPage may still emit slugs)
 *   /bodha-v2?book=…&mode=preview   → mode=preview is ignored (v1)
 *
 * Local state (icraftz.bodha.choices.v1):
 *   { "<bookId>": { resonates: [1,3], saveForLater: [2], notForMe: [4] } }
 *
 * Guardrails honoured:
 *   - Zero backend / auth / engine / admin changes.
 *   - /bodha route untouched (staged at /bodha-v2 only).
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Heart, Bookmark, X as XIcon, ArrowRight, RefreshCw } from "lucide-react";

import Navigation from "../components/homev3/Navigation";
import Footer from "../components/homev3/Footer";
import GoldRule from "../components/homev3/GoldRule";
import ParchmentBackground from "../components/ParchmentBackground";
import { useLanguage } from "../lib/useLanguage";
import { withTransientRetry } from "../lib/apiRetry";
import { T, R } from "../components/homev3/tokens";
import {
  CARD_BG,
  CARD_BORDER,
  CARD_RADIUS,
  CARD_SHADOW,
  CARD_SHADOW_HOVER,
  StageKicker,
  StageLeftIllustration,
  StageEmptyStateFlank,
} from "../components/homev3/parchmentCard";
import { resolveBook } from "../lib/bookLookup";
import { useAuth } from "../context/AuthContext";
import InfographicEntryButton from "../components/InfographicEntryButton";
import { translatingLine } from "../lib/translatingLine";

// ─────────────────────────────────────────────────────────────────────
//  Constants
// ─────────────────────────────────────────────────────────────────────

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/mycraftz-install`;
const LS_KEY = "icraftz.bodha.choices.v1";
const IDEAS_COUNT = 10;
const AXIOS_TIMEOUT_MS = 60000;
const DEBOUNCE_MS = 200;

const CHOICE = {
  RESONATES: "resonates",
  SAVE:      "saveForLater",
  NOT:       "notForMe",
};

// ─────────────────────────────────────────────────────────────────────
//  LocalStorage helpers (SSR-safe)
// ─────────────────────────────────────────────────────────────────────

function readChoices() {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(LS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function writeChoices(all) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(LS_KEY, JSON.stringify(all));
  } catch {
    /* quota or private-mode — safely ignore */
  }
}

// ─────────────────────────────────────────────────────────────────────
//  Presentational: diya (candle) flame — CSS-only, subtle
// ─────────────────────────────────────────────────────────────────────

function Diya() {
  return (
    <div
      aria-hidden="true"
      style={{ display: "inline-flex", alignItems: "flex-end", gap: 6, verticalAlign: "middle" }}
    >
      <span
        style={{
          display: "inline-block",
          width: 10,
          height: 14,
          borderRadius: "50% 50% 45% 45% / 60% 60% 40% 40%",
          background: "radial-gradient(circle at 50% 35%, #FFE9A6 0%, #E4A82C 55%, #8B5A22 100%)",
          animation: "diya-flicker 1.6s ease-in-out infinite alternate",
          transformOrigin: "50% 100%",
        }}
      />
      <span
        style={{
          display: "inline-block",
          width: 26,
          height: 6,
          borderRadius: "50%",
          background: "linear-gradient(180deg, #6b3d1a 0%, #3a1e0c 100%)",
          boxShadow: "inset 0 -1px 0 rgba(0,0,0,0.35)",
        }}
      />
      <style>{`
        @keyframes diya-flicker {
          0%   { transform: scale(1)   translateY(0);    filter: brightness(1); }
          50%  { transform: scale(1.06) translateY(-1px); filter: brightness(1.12); }
          100% { transform: scale(0.98) translateY(1px);  filter: brightness(0.94); }
        }
      `}</style>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
//  Presentational: Artefact card (both skeleton + real)
// ─────────────────────────────────────────────────────────────────────

function ArtefactCard({ idea, index, choice, onChoose, isSkeleton }) {
  const [hover, setHover] = useState(false);

  const cardStyle = {
    position: "relative",
    background: CARD_BG,
    border: CARD_BORDER,
    borderRadius: CARD_RADIUS,
    padding: "20px 20px 16px",
    boxShadow: hover ? CARD_SHADOW_HOVER : CARD_SHADOW,
    transform: hover ? "translateY(-2px)" : "translateY(0)",
    transition: "transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease",
    minHeight: 200,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    gap: 14,
  };

  if (isSkeleton) {
    return (
      <div
      style={{ ...cardStyle, transform: "none", boxShadow: CARD_SHADOW }}
        aria-hidden="true"
      >
        <div>
          <div style={skelBar(90, 14, 10)} />
          <div style={skelBar(70, 18, 14)} />
          <div style={skelBar(100, 12, 6)} />
          <div style={skelBar(85, 12, 6)} />
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <div style={skelBar(50, 26, 0)} />
          <div style={skelBar(50, 26, 0)} />
          <div style={skelBar(50, 26, 0)} />
        </div>
        <style>{`
          @keyframes bodha-shimmer {
            0%   { background-position: -200px 0; }
            100% { background-position: 200px 0; }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div
      data-testid={`bodha-artefact-${idea.id}`}
      style={cardStyle}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      {/* Ordinal + title + description */}
      <div>
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            gap: 10,
            marginBottom: 8,
          }}
        >
          <span
            style={{
              fontFamily: T.F_HEADING,
              fontSize: 11,
              letterSpacing: "0.16em",
              color: T.GOLD,
            }}
          >
            {String(index + 1).padStart(2, "0")}
          </span>
          <span style={{ flex: 1, height: 1, background: `linear-gradient(to right, ${T.GOLD}, transparent)` }} />
        </div>
        <h3
          className="m-0"
          style={{
            fontFamily: T.F_HEADING,
            fontSize: 18,
            fontWeight: 600,
            color: T.DARK_BROWN,
            lineHeight: 1.25,
            marginBottom: 8,
          }}
        >
          {idea.title}
        </h3>
        <p
          className="m-0"
          style={{
            fontFamily: T.F_BODY,
            fontSize: 14,
            fontStyle: "italic",
            color: T.MUTED_BROWN,
            lineHeight: 1.45,
          }}
        >
          {idea.description}
        </p>
      </div>

      {/* Choice buttons */}
      <div style={{ display: "flex", gap: 8 }}>
        <ChoiceBtn
          testId={`bodha-artefact-choice-${idea.id}-resonates`}
          label="Resonates"
          icon={<Heart size={13} strokeWidth={1.75} />}
          active={choice === CHOICE.RESONATES}
          activeBg="#8B3A12"
          activeFg={T.PARCHMENT_LT}
          onClick={() => onChoose(idea.id, CHOICE.RESONATES)}
        />
        <ChoiceBtn
          testId={`bodha-artefact-choice-${idea.id}-save`}
          label="Save"
          icon={<Bookmark size={13} strokeWidth={1.75} />}
          active={choice === CHOICE.SAVE}
          activeBg={T.DARK_BROWN}
          activeFg={T.PARCHMENT_LT}
          onClick={() => onChoose(idea.id, CHOICE.SAVE)}
        />
        <ChoiceBtn
          testId={`bodha-artefact-choice-${idea.id}-not`}
          label="Not for me"
          icon={<XIcon size={13} strokeWidth={1.75} />}
          active={choice === CHOICE.NOT}
          activeBg="#7A6B4E"
          activeFg={T.PARCHMENT_LT}
          onClick={() => onChoose(idea.id, CHOICE.NOT)}
        />
      </div>
    </div>
  );
}

function skelBar(widthPct, height, mb) {
  return {
    width: `${widthPct}%`,
    height,
    marginBottom: mb,
    borderRadius: 4,
    background:
      "linear-gradient(90deg, rgba(139,99,71,0.08) 0%, rgba(139,99,71,0.18) 50%, rgba(139,99,71,0.08) 100%)",
    backgroundSize: "400px 100%",
    animation: "bodha-shimmer 1.4s ease-in-out infinite",
  };
}

function ChoiceBtn({ testId, label, icon, active, activeBg, activeFg, onClick }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button"
      data-testid={testId}
      data-active={active}
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        flex: 1,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 6,
        padding: "7px 8px",
        fontFamily: T.F_HEADING,
        fontSize: 11,
        letterSpacing: "0.06em",
        borderRadius: 999,
        border: `1px solid ${active ? activeBg : T.GOLD}`,
        background: active ? activeBg : hover ? "rgba(201,168,76,0.15)" : "transparent",
        color: active ? activeFg : T.DARK_BROWN,
        cursor: "pointer",
        transition: "background 140ms ease, color 140ms ease, border-color 140ms ease",
      }}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────
//  Left panel — Current Book
// ─────────────────────────────────────────────────────────────────────

function CurrentBookPanel({ book, savedCount }) {
  return (
    <aside
      data-testid="bodha-current-book"
      className="bodha-current-book"
      style={{
        background: CARD_BG,
        border: CARD_BORDER,
        borderRadius: CARD_RADIUS,
        padding: 20,
        boxShadow: CARD_SHADOW,
        position: "sticky",
        top: 84,
        maxHeight: "calc(100vh - 108px)",
        minWidth: 0,
      }}
    >
      <p
        className="m-0"
        style={{
          fontFamily: T.F_HEADING,
          fontSize: 11,
          letterSpacing: "0.16em",
          color: T.GOLD,
          textAlign: "center",
          marginBottom: 14,
        }}
      >
        CURRENT BOOK
      </p>

      <div
        style={{
          aspectRatio: "3/4",
          background: "#EADFC4",
          overflow: "hidden",
          borderRadius: 6,
          border: `1px solid ${T.GOLD}`,
          marginBottom: 14,
        }}
      >
        <img
          src={book.coverUrl || book.image}
          alt={book.title}
          style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
        />
      </div>

      <h2
        className="m-0"
        style={{
          fontFamily: T.F_HEADING,
          fontSize: 18,
          fontWeight: 700,
          color: T.DARK_BROWN,
          lineHeight: 1.2,
          textAlign: "center",
          marginBottom: 4,
        }}
      >
        {book.title}
      </h2>
      <p
        className="m-0"
        style={{
          fontFamily: T.F_BODY,
          fontStyle: "italic",
          fontSize: 13,
          color: T.MUTED_BROWN,
          textAlign: "center",
          marginBottom: 14,
        }}
      >
        {book.author}
      </p>

      <div style={{ display: "flex", justifyContent: "center", marginBottom: 14 }}>
        <GoldRule />
      </div>

      <p
        className="m-0"
        style={{
          fontFamily: T.F_BODY,
          fontSize: 13,
          color: T.DARK_BROWN,
          textAlign: "center",
          marginBottom: 6,
        }}
      >
        <strong>{IDEAS_COUNT}</strong> Wisdom Artefacts
      </p>
      <p
        className="m-0"
        style={{
          fontFamily: T.F_BODY,
          fontStyle: "italic",
          fontSize: 12,
          color: T.MUTED_BROWN,
          textAlign: "center",
          marginBottom: 16,
        }}
        data-testid="bodha-saved-counter"
      >
        Saved {savedCount} / {IDEAS_COUNT}
      </p>

      <InfographicEntryButton book={book} />

      <Link
        to={R.library}
        data-testid="bodha-change-book"
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 6,
          width: "100%",
          padding: "10px 12px",
          fontFamily: T.F_HEADING,
          fontSize: 12,
          letterSpacing: "0.08em",
          color: T.DARK_BROWN,
          textDecoration: "none",
          borderRadius: 999,
          border: `1px solid ${T.GOLD}`,
          background: "transparent",
          transition: "background 140ms ease",
        }}
        onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(201,168,76,0.15)")}
        onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
      >
        Change book <ArrowRight size={12} strokeWidth={1.75} />
      </Link>
    </aside>
  );
}

// ─────────────────────────────────────────────────────────────────────
//  Empty state (no ?book= param)
// ─────────────────────────────────────────────────────────────────────

function EmptyState() {
  return (
    <div
      data-testid="bodha-empty-state"
      style={{
        maxWidth: 620,
        margin: "0 auto",
        textAlign: "center",
        padding: "80px 24px",
      }}
    >
      <div style={{ display: "flex", justifyContent: "center", marginBottom: 18 }}>
        <GoldRule wide />
      </div>
      <StageKicker stage="bodha" text="BODHA · बोध" size="large" testId="bodha-empty-kicker" />
      <h1
        className="m-0 bodha-empty-h1"
        style={{
          fontFamily: T.F_DISPLAY,
          fontSize: 44,
          fontWeight: 700,
          color: T.DARK_BROWN,
          lineHeight: 1.1,
          marginBottom: 14,
        }}
      >
        Choose a book to begin understanding.
      </h1>
      <p
        className="m-0 bodha-empty-sub"
        style={{
          fontFamily: T.F_BODY,
          fontStyle: "italic",
          fontSize: 17,
          color: T.MUTED_BROWN,
          lineHeight: 1.55,
          marginBottom: 26,
        }}
      >
        Bodha is where great ideas are revealed as your own wisdom artefacts.
        Pick any book from the Library to see its ten installable ideas.
      </p>
      <Link
        to={R.library}
        data-testid="bodha-empty-cta"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          padding: "12px 26px",
          fontFamily: T.F_HEADING,
          fontSize: 13,
          letterSpacing: "0.10em",
          color: T.PARCHMENT_LT,
          textDecoration: "none",
          borderRadius: 999,
          background: T.DARK_BROWN,
          border: `1px solid ${T.DARK_BROWN}`,
          boxShadow: "0 3px 10px rgba(44,24,16,0.20)",
        }}
      >
        Explore the Library <ArrowRight size={14} strokeWidth={1.75} />
      </Link>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
//  Error state
// ─────────────────────────────────────────────────────────────────────

function ErrorState({ bookTitle, onRetry }) {
  return (
    <div
      data-testid="bodha-error-state"
      style={{
        maxWidth: 520,
        margin: "60px auto",
        textAlign: "center",
        padding: "40px 24px",
        background: CARD_BG,
        border: CARD_BORDER,
        borderRadius: CARD_RADIUS,
        boxShadow: CARD_SHADOW,
      }}
    >
      <p
        className="m-0"
        style={{
          fontFamily: T.F_HEADING,
          fontSize: 11,
          letterSpacing: "0.18em",
          color: "#8B3A12",
          marginBottom: 10,
        }}
      >
        UNABLE TO CRAFT ARTEFACTS
      </p>
      <p
        className="m-0"
        style={{
          fontFamily: T.F_BODY,
          fontStyle: "italic",
          fontSize: 15,
          color: T.MUTED_BROWN,
          marginBottom: 22,
        }}
      >
        The oracle went quiet while consulting {bookTitle}. Try again in a moment.
      </p>
      <button
        type="button"
        data-testid="bodha-retry"
        onClick={onRetry}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          padding: "10px 22px",
          fontFamily: T.F_HEADING,
          fontSize: 12,
          letterSpacing: "0.10em",
          color: T.PARCHMENT_LT,
          background: T.DARK_BROWN,
          border: `1px solid ${T.DARK_BROWN}`,
          borderRadius: 999,
          cursor: "pointer",
        }}
      >
        <RefreshCw size={13} strokeWidth={1.75} /> Try again
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
//  Loading banner (with diya)
// ─────────────────────────────────────────────────────────────────────

function LoadingBanner({ bookTitle }) {
  return (
    <div
      data-testid="bodha-loading-banner"
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 12,
        margin: "0 auto 22px",
        padding: "10px 22px",
        maxWidth: 620,
        background: "rgba(240,230,204,0.6)",
        border: `1px dashed ${T.GOLD}`,
        borderRadius: 999,
      }}
    >
      <Diya />
      <p
        className="m-0"
        style={{
          fontFamily: T.F_BODY,
          fontStyle: "italic",
          fontSize: 14,
          color: T.DARK_BROWN,
        }}
      >
        Crafting your Wisdom Artefacts from <strong>{bookTitle}</strong>…
      </p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
//  Main page
// ─────────────────────────────────────────────────────────────────────

export default function BodhaPage() {
  const [params] = useSearchParams();
  const bookParam = params.get("book");
  const book = useMemo(() => resolveBook(bookParam), [bookParam]);

  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // ── Language-aware artefact translation ──────────────────────────
  // Ideas are always fetched in English from /ideas (their ids drive
  // downstream Sankalpa /why /plan calls). When the user has a non-EN
  // language selected, we call /ideas-translate to get a per-language
  // version of title+description, keyed off the English ideas. Cached
  // per session by (bookId × lang).
  const [lang] = useLanguage();
  const { isAuthenticated, authHeaders } = useAuth();
  const showcaseSlug = params.get("showcase");
  const [showcasePick, setShowcasePick] = useState(null); // { name, ideaId|null }
  const [translatedIdeas, setTranslatedIdeas] = useState(null);
  const [isTranslatingIdeas, setIsTranslatingIdeas] = useState(false);
  const translateCacheRef = useRef({}); // { `${bookId}::${lang}` → ideas[] }

  // Choices for THIS book — keyed by String(book.id) in storage
  const [choicesForBook, setChoicesForBook] = useState({ resonates: [], saveForLater: [], notForMe: [] });
  const debounceTimer = useRef(null);

  // ── Load stored choices for this book on mount / book change ──────
  // localStorage paints instantly; the server (source of truth) then wins.
  const [choicesHydrated, setChoicesHydrated] = useState(false);
  useEffect(() => {
    if (!book) return;
    const all = readChoices();
    const entry = all[String(book.id)] || {};
    const local = {
      resonates:    Array.isArray(entry.resonates)    ? entry.resonates    : [],
      saveForLater: Array.isArray(entry.saveForLater) ? entry.saveForLater : [],
      notForMe:     Array.isArray(entry.notForMe)     ? entry.notForMe     : [],
    };
    setChoicesForBook(local);
    setChoicesHydrated(!isAuthenticated);
    if (!isAuthenticated) return;
    let alive = true;
    axios.get(`${API_BASE}/journey-state`, {
      params: { book_id: book.id, targetLanguage: lang }, headers: authHeaders(),
    }).then((r) => {
      if (!alive) return;
      const srv = r.data?.bodha || {};
      const hasServer = ["resonates", "saveForLater", "notForMe"].some((k) => (srv[k] || []).length);
      const hasLocal = Object.values(local).some((v) => v.length);
      if (hasServer || !hasLocal) {
        const next = { resonates: srv.resonates || [], saveForLater: srv.saveForLater || [], notForMe: srv.notForMe || [] };
        setChoicesForBook(next);
        const cache = readChoices();
        cache[String(book.id)] = next;
        writeChoices(cache);
      }
    }).catch(() => {}).finally(() => { if (alive) setChoicesHydrated(true); });
    return () => { alive = false; };
  }, [book, isAuthenticated, lang, authHeaders]);

  // ── Fetch 10 ideas — once per book id per mount ───────────────────
  const fetchIdeas = useCallback(async () => {
    if (!book) return;
    setLoading(true);
    setError(null);
    setIdeas([]);
    const snapshot = {
      id:          String(book.id),
      title:       book.title || "",
      author:      book.author || "",
      summary:     book.summary || "",
      insights:    Array.isArray(book.insights)   ? book.insights   : [],
      actionItems: Array.isArray(book.actionItems) ? book.actionItems : [],
      coreTheme:   book.coreTheme || "",
    };
    try {
      const res = await axios.post(
        `${API_BASE}/ideas`,
        { book: snapshot },
        { timeout: AXIOS_TIMEOUT_MS }
      );
      const list = Array.isArray(res.data?.ideas) ? res.data.ideas : [];
      setIdeas(list.slice(0, IDEAS_COUNT));
    } catch (e) {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [book]);

  useEffect(() => {
    if (!book) return;
    fetchIdeas();
    // Cleanup: pending debounce writes are flushed on unmount below.
  }, [book, fetchIdeas]);

  // ── Translate ideas when non-English lang is active ──────────────
  useEffect(() => {
    if (!book) return;
    if (!Array.isArray(ideas) || ideas.length === 0) {
      setTranslatedIdeas(null);
      setIsTranslatingIdeas(false);
      return;
    }
    if (lang === "en") {
      setTranslatedIdeas(null);
      setIsTranslatingIdeas(false);
      return;
    }
    const cacheKey = `${book.id}::${lang}`;
    if (translateCacheRef.current[cacheKey]) {
      setTranslatedIdeas(translateCacheRef.current[cacheKey]);
      setIsTranslatingIdeas(false);
      return;
    }
    let cancelled = false;
    setIsTranslatingIdeas(true);
    (async () => {
      try {
        const res = await withTransientRetry(() =>
          axios.post(
            `${API_BASE}/ideas-translate`,
            {
              book_id: String(book.id),
              book_title: book.title || "",
              targetLanguage: lang,
              ideas: ideas.map((i) => ({
                id: i.id,
                title: i.title || "",
                description: i.description || "",
              })),
            },
            { timeout: AXIOS_TIMEOUT_MS }
          )
        );
        if (cancelled) return;
        const payload = res.data;
        // Fail-open: backend returns lang:"en" + translation_failed:true
        // if Anthropic hiccups; leave English rendered in that case.
        if (payload?.lang && payload.lang === lang && Array.isArray(payload.ideas)) {
          translateCacheRef.current[cacheKey] = payload.ideas;
          setTranslatedIdeas(payload.ideas);
        } else {
          setTranslatedIdeas(null);
        }
      } catch (e) {
        if (!cancelled) {
          setTranslatedIdeas(null);
        }
      } finally {
        if (!cancelled) setIsTranslatingIdeas(false);
      }
    })();
    return () => { cancelled = true; };
  }, [book, ideas, lang]);

  // What we actually render — a translated array indexed by original id
  // if one is available; otherwise the English ideas straight through.
  const displayIdeas = React.useMemo(() => {
    if (!translatedIdeas || !Array.isArray(ideas) || ideas.length === 0) {
      return ideas;
    }
    const byId = new Map(translatedIdeas.map((t) => [Number(t.id), t]));
    return ideas.map((eng) => {
      const tr = byId.get(Number(eng.id));
      return tr
        ? { ...eng, title: tr.title || eng.title, description: tr.description || eng.description }
        : eng;
    });
  }, [ideas, translatedIdeas]);

  // ── Choice handler — mutually exclusive per idea, debounced write ─
  const handleChoose = useCallback(
    (ideaId, choiceKey) => {
      // Server-side record of the decision (logged-in readers only).
      if (book && isAuthenticated) {
        const alreadySame = choicesForBook[choiceKey]?.includes(ideaId);
        axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/mycraftz-install/bodha/choice`,
          {
            book: { id: String(book.id), title: book.title, author: book.author || "" },
            idea_id: ideaId,
            choice: alreadySame ? null : choiceKey,
            targetLanguage: lang,
          },
          { headers: authHeaders() }
        ).catch(() => {});
      }
      setChoicesForBook((prev) => {
        // Remove this idea from all three arrays first
        const stripped = {
          resonates:    prev.resonates.filter((x) => x !== ideaId),
          saveForLater: prev.saveForLater.filter((x) => x !== ideaId),
          notForMe:     prev.notForMe.filter((x) => x !== ideaId),
        };
        // Toggle: if the user re-clicks the same choice, un-set it
        const alreadySame = prev[choiceKey]?.includes(ideaId);
        const next = alreadySame
          ? stripped
          : { ...stripped, [choiceKey]: [...stripped[choiceKey], ideaId] };

        // Debounced localStorage write
        if (debounceTimer.current) clearTimeout(debounceTimer.current);
        debounceTimer.current = setTimeout(() => {
          if (!book) return;
          const all = readChoices();
          all[String(book.id)] = next;
          writeChoices(all);
        }, DEBOUNCE_MS);

        return next;
      });
    },
    [book, isAuthenticated, authHeaders, lang, choicesForBook]
  );

  // Arriving from a showcase "Install": pre-mark the matching idea as
  // resonating (never auto-committed — Sankalpa remains the user's choice).
  const showcaseDone = useRef(false);
  useEffect(() => {
    if (!showcaseSlug || showcaseDone.current || !choicesHydrated || ideas.length === 0) return;
    showcaseDone.current = true;
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/mycraftz/showcase/${showcaseSlug}`)
      .then((r) => {
        const name = r.data?.craftprint?.idea_name || "";
        if (!name) return;
        const words = (t) => new Set(String(t).toLowerCase().match(/[a-z0-9]{4,}/g) || []);
        const target = words(name);
        let best = null, score = 0;
        ideas.forEach((idea) => {
          const w = words(`${idea.title} ${idea.description || ""}`);
          const hit = [...target].filter((x) => w.has(x)).length;
          if (hit > score) { score = hit; best = idea; }
        });
        setShowcasePick({ name, ideaId: best ? best.id : null });
        if (best && !choicesForBook.resonates.includes(best.id)) handleChoose(best.id, "resonates");
      })
      .catch(() => {});
  }, [showcaseSlug, choicesHydrated, ideas, choicesForBook, handleChoose]);

  // Flush any pending write when the component unmounts
  useEffect(() => {
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, []);

  const choiceForIdea = useCallback(
    (ideaId) => {
      if (choicesForBook.resonates.includes(ideaId))    return CHOICE.RESONATES;
      if (choicesForBook.saveForLater.includes(ideaId)) return CHOICE.SAVE;
      if (choicesForBook.notForMe.includes(ideaId))     return CHOICE.NOT;
      return null;
    },
    [choicesForBook]
  );

  const savedCount =
    choicesForBook.resonates.length +
    choicesForBook.saveForLater.length +
    choicesForBook.notForMe.length;

  // ────────────────────────────────────────────────────────────────
  //  RENDER
  // ────────────────────────────────────────────────────────────────

  return (
    <div
      data-testid="bodha-page"
      className="min-h-screen flex flex-col bodha-page-root"
      style={{ background: "transparent" }}
    >
      <ParchmentBackground />
      <Navigation activePath={R.bodha} />

      {/* ── Mobile responsive pass (<900px) ── matches the homepage's
             tuned mobile pattern: single-column stack, narrower gaps,
             CurrentBook scroll becomes full-width above the artefacts. */}
      <style>{`
        .bodha-page-root { overflow-x: hidden; }
        @media (max-width: 899px) {
          .stage-flank, .stage-left-illustration, .stage-empty-flank { display: none !important; }
          .bodha-empty-wrap { min-height: 0 !important; }
          .bodha-shell {
            grid-template-columns: 1fr !important;
            gap: 24px !important;
            padding: 0 20px !important;
            width: 100% !important;
            max-width: 100% !important;
          }
          .bodha-shell > * { min-width: 0 !important; }
          .bodha-current-book {
            position: static !important;
            max-height: none !important;
            max-width: 320px !important;
            margin: 0 auto !important;
            width: 100% !important;
          }
          [data-testid="bodha-artefact-grid"] {
            grid-template-columns: 1fr !important;
            gap: 14px !important;
          }
          [data-testid="bodha-artefact-grid"] > * { min-width: 0 !important; }
          .bodha-header-h1 {
            font-size: 30px !important;
            line-height: 1.15 !important;
          }
          .bodha-header-sub {
            font-size: 14px !important;
          }
          .bodha-empty-h1 {
            font-size: 32px !important;
          }
          .bodha-empty-sub {
            font-size: 15px !important;
          }
        }
      `}</style>

      <main
        className="flex-1 w-full"
        style={{ paddingTop: 88, paddingBottom: 64, background: "transparent" }}
      >
        {!book ? (
          <div className="bodha-empty-wrap" style={{ position: "relative", minHeight: 1025 }}>
            <StageEmptyStateFlank stage="bodha" />
            <EmptyState />
          </div>
        ) : (
          <div
            className="bodha-shell"
            style={{
              position: "relative",
              maxWidth: 1240,
              margin: "0 auto",
              padding: "0 32px",
              display: "grid",
              gridTemplateColumns: "260px 1fr",
              gap: 40,
              alignItems: "flex-start",
            }}
          >
            {/* ── Left: Current book panel + decorative illustration ─── */}
            <div
              className="bodha-left-col"
              style={{
                alignSelf: "stretch",
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <CurrentBookPanel book={book} savedCount={savedCount} />
              <StageLeftIllustration stage="bodha" />
            </div>

            {/* ── Right: header + 10 artefacts (or skeletons / error) */}
            <section>
              {showcasePick && (
                <p
                  data-testid="bodha-showcase-note"
                  style={{
                    margin: "0 0 18px", padding: "10px 14px", borderRadius: 10,
                    background: "rgba(255,252,244,.72)", border: "1px solid rgba(201,168,76,.3)",
                    fontFamily: "Georgia, serif", fontStyle: "italic", fontSize: 14, color: "#5c4033", textAlign: "center",
                  }}
                >
                  {showcasePick.ideaId
                    ? <>From the showcase: “{showcasePick.name}” — marked as resonating below. Choose your three in Sankalpa.</>
                    : <>From the showcase: “{showcasePick.name}” — mark the idea closest to it as resonating.</>}
                </p>
              )}
              {/* Header */}
              <div style={{ textAlign: "center", marginBottom: 22 }}>
                <div style={{ display: "flex", justifyContent: "center", marginBottom: 12 }}>
                  <GoldRule wide />
                </div>
                <StageKicker stage="bodha" text="BODHA · बोध" testId="bodha-header-kicker" />
                <h1
                  className="m-0 bodha-header-h1"
                  style={{
                    fontFamily: T.F_DISPLAY,
                    fontSize: 40,
                    fontWeight: 700,
                    color: T.DARK_BROWN,
                    lineHeight: 1.1,
                    marginBottom: 10,
                  }}
                >
                  Understanding what truly matters.
                </h1>
                <p
                  className="m-0 bodha-header-sub"
                  style={{
                    fontFamily: T.F_BODY,
                    fontStyle: "italic",
                    fontSize: 16,
                    color: T.MUTED_BROWN,
                    lineHeight: 1.55,
                    maxWidth: 620,
                    margin: "0 auto 18px",
                  }}
                >
                  Ten installable ideas from <strong>{book.title}</strong> — revealed as
                  wisdom artefacts. Choose the ones that resonate; they will seed your
                  Sankalpa.
                </p>
              </div>

              {loading && <LoadingBanner bookTitle={book.title} />}
              {error && !loading && (
                <ErrorState bookTitle={book.title} onRetry={fetchIdeas} />
              )}

              {/* Translation status — appears only while /ideas-translate
                  is in flight and never on English or during the initial
                  /ideas load. Matches the parchment aesthetic used on
                  BookDetailPage. */}
              {!loading && !error && lang !== "en" && isTranslatingIdeas && (
                <div
                  data-testid="bodha-translating-banner"
                  style={{
                    margin: "0 auto 16px",
                    padding: "8px 14px",
                    maxWidth: 640,
                    fontFamily: "'Cormorant Garamond', Georgia, serif",
                    fontSize: "0.95rem",
                    fontStyle: "italic",
                    color: "#5C4A3A",
                    background: "rgba(201,168,76,0.10)",
                    border: "1px solid rgba(201,168,76,0.45)",
                    borderRadius: 4,
                    textAlign: "center",
                  }}
                >
                  {translatingLine(lang)}
                </div>
              )}

              {/* Artefact grid — skeletons while loading, real when ready */}
              {(loading || ideas.length > 0) && !error && (
                <div
                  data-testid="bodha-artefact-grid"
                  className="grid gap-5"
                  style={{
                    gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
                  }}
                >
                  {loading
                    ? Array.from({ length: IDEAS_COUNT }).map((_, i) => (
                        <ArtefactCard key={`skel-${i}`} isSkeleton />
                      ))
                    : displayIdeas.map((idea, i) => (
                        <ArtefactCard
                          key={idea.id}
                          idea={idea}
                          index={i}
                          choice={choiceForIdea(idea.id)}
                          onChoose={handleChoose}
                        />
                      ))}
                </div>
              )}

              {/* Forward CTA — routes to Sankalpa (currently staged at
                  /sankalpa-v2; will repoint to /sankalpa after the flip).
                  Shown only when ideas have loaded. */}
              {ideas.length > 0 && !loading && !error && (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 8,
                    marginTop: 44,
                  }}
                >
                  <Link
                    to={`/sankalpa?book=${book.id}`}
                    data-testid="bodha-commit-cta"
                    title="Continue to Sankalpa"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "12px 28px",
                      fontFamily: T.F_HEADING,
                      fontSize: 13,
                      letterSpacing: "0.10em",
                      color: T.PARCHMENT_LT,
                      textDecoration: "none",
                      background: T.DARK_BROWN,
                      border: `1px solid ${T.DARK_BROWN}`,
                      borderRadius: 999,
                      boxShadow: "0 3px 10px rgba(44,24,16,0.20)",
                      transition: "transform 140ms ease, box-shadow 140ms ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-1px)";
                      e.currentTarget.style.boxShadow = "0 6px 14px rgba(44,24,16,0.28)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = "0 3px 10px rgba(44,24,16,0.20)";
                    }}
                  >
                    Commit &middot; Sankalpa <ArrowRight size={14} strokeWidth={1.75} />
                  </Link>
                  <p
                    className="m-0"
                    style={{
                      fontFamily: T.F_BODY,
                      fontStyle: "italic",
                      fontSize: 12,
                      color: T.MUTED_BROWN,
                      letterSpacing: "0.02em",
                    }}
                  >
                    Choose three ideas to seal your Sankalpa.
                  </p>
                </div>
              )}
            </section>
          </div>
        )}
      </main>

      <Footer activePath={R.bodha} />
    </div>
  );
}
